import { AppOrderSummary } from "../../../../interfaces/sales";
import { formatDate, getLocalFromUtc } from "../../../../utils/dates";
import {
  getTranslatedAppOrderDeliveryMethod,
  getTranslatedAppOrderType,
} from "../../../../translates/sales/sales";
import { AppOrderField } from "../interfaces";

const RUNNER_TO_SEAT_METHOD = "runner_to_seat";

// Statuses that already finished well, that failed, and that wait on the customer's money;
// anything else is an order still being fulfilled.
const FINISHED_STATUSES: string[] = ["delivered", "picked_up"];
const FAILED_STATUSES: string[] = ["cancelled", "payment_failed"];
const PENDING_STATUSES: string[] = [
  "pending_payment",
  "refund_pending",
  "refunded",
];

const formatDateTime = (date?: string | null): string =>
  date ? `${formatDate(getLocalFromUtc(date))} hrs.` : "-";

const getDeliveryTargetFields = (appOrder: AppOrderSummary): AppOrderField[] => {
  if (appOrder.delivery_method !== RUNNER_TO_SEAT_METHOD) {
    return [
      {
        label: "Punto de recolección",
        value: appOrder.pickup_point_name || "-",
      },
    ];
  }

  const seat: string = `${appOrder.seat_row ?? ""}${appOrder.seat_number ?? ""}`;

  return [
    { label: "Sección", value: appOrder.section_name || "-" },
    { label: "Asiento", value: seat || "-" },
  ];
};

/**
 * Tone the app order status is painted with. YOSHI-10444: named apart from the Tailwind classes so
 * the PDF export can paint its chip the same color without repeating the status buckets.
 */
export type AppOrderStatusTone = "green" | "red" | "yellow" | "blue";

export const getAppOrderStatusTone = (status: string): AppOrderStatusTone => {
  if (FINISHED_STATUSES.includes(status)) {
    return "green";
  }
  if (FAILED_STATUSES.includes(status)) {
    return "red";
  }
  if (PENDING_STATUSES.includes(status)) {
    return "yellow";
  }
  return "blue";
};

const TONE_BADGE_CLASS: Record<AppOrderStatusTone, string> = {
  green: "bg-green-100 text-green-800",
  red: "bg-red-100 text-red-800",
  yellow: "bg-yellow-100 text-yellow-800",
  blue: "bg-blue-100 text-blue-800",
};

export const getAppOrderStatusBadgeClass = (status: string): string =>
  TONE_BADGE_CLASS[getAppOrderStatusTone(status)];

export const getAppOrderFields = (
  appOrder: AppOrderSummary,
): AppOrderField[] => {
  const fields: AppOrderField[] = [
    {
      label: "Modalidad",
      value: getTranslatedAppOrderType(appOrder.order_type) || "-",
    },
    {
      label: "Método de entrega",
      value: getTranslatedAppOrderDeliveryMethod(appOrder.delivery_method),
    },
    { label: "Palabra clave", value: appOrder.delivery_keyword || "-" },
    { label: "Zona de servicio", value: appOrder.service_zone_name || "-" },
    ...getDeliveryTargetFields(appOrder),
  ];

  // YOSHI-10470: the hour the customer was promised, which is what operations has to deliver at.
  // It used to read scheduled_delivery_time — the moment the KITCHEN must start, earlier than the
  // delivery hour and only persisted once the production scheduler is armed, so a healthy pre-order
  // could reach this detail with the field empty.
  if (appOrder.delivery_due_at) {
    fields.push({
      label: "Programado para",
      value: formatDateTime(appOrder.delivery_due_at),
    });
  }

  return fields;
};
